import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Stack,
  InputAdornment,
  FormControl,
} from "@mui/material";
import {
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SaveChangingsButton from "../AccountSetting/SaveChangingsButton";
import CameraIcon from "../../UI/CameraIcon";
// import AddIcon from "@mui/icons-material/Add";
import PhoneInput from "react-phone-input-2";
import CompaniesNavbar from "./CompaniesNavbar";
import FooterSection from "../JobBoardHomepage/Footer/FooterSection";
import JoinPitchersDark from "./JoinPitchersDark";
const industries = [
  "Technology",
  "Finance",
  "Healthcare",
  "Education",
  "Retail",
  "Manufacturing",
  "Media",
  "Entertainment",
  "Food",
  "Transportation",
  "Construction",
  "Real Estate",
  "Energy",
  "Agriculture",
  "Other",
];

const CreateCompany = ({ editMode = false, existingCompany = null }) => {
  const [company, setCompany] = useState(
    existingCompany || {
      name: "",
      description: "",
      industry: "",
      location: "",
      website: "",
      logo: "",
      teamMembers: [],
    }
  );

  const [newMember, setNewMember] = useState({
    email: "",
    role: "staff",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!company.name) newErrors.name = "Company name is required";
    if (!company.description)
      newErrors.description = "Company description is required";
    if (!company.industry) newErrors.industry = "Industry is required";
    if (!company.location) newErrors.location = "Location is required";
    if (
      company.website &&
      !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/.test(
        company.website
      )
    ) {
      newErrors.website = "Please enter a valid website URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompany({ ...company, [name]: value });

    // Clear the error for this field if it exists
    if (errors[name]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleNewMemberChange = (e) => {
    const { name, value } = e.target;
    setNewMember({ ...newMember, [name]: value });
  };

  const handleAddMember = () => {
    if (!newMember.email) {
      setErrors({ ...errors, teamMember: "Email is required" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email)) {
      setErrors({
        ...errors,
        teamMember: "Please enter a valid email address",
      });
      return;
    }

    setCompany({
      ...company,
      teamMembers: [
        ...company.teamMembers,
        { ...newMember, id: Date.now().toString() },
      ],
    });

    setNewMember({
      email: "",
      role: "staff",
    });

    setErrors({ ...errors, teamMember: undefined });
  };

  const handleRemoveMember = (id) => {
    setCompany({
      ...company,
      teamMembers: company.teamMembers.filter((member) => member.id !== id),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // In a real app, this would save the company to the backend
      console.log("Company data:", company);
      setSuccess(true);

      // Reset form after success (only for create mode)
      if (!editMode) {
        setCompany({
          name: "",
          description: "",
          industry: "",
          location: "",
          website: "",
          logo: "",
          teamMembers: [],
        });
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    }
  };

  return (
    <>
      <CompaniesNavbar />
      <Stack sx={{ p: 4, bgcolor: "#1c1c1c" }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            color: "#ffffff",
            fontFamily: "Inter",
          }}
        >
          <BusinessIcon sx={{ mr: 1, color: "#FDDF59" }} />
          {editMode ? "Edit Company" : "Create New Company"}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Company {editMode ? "updated" : "created"} successfully!
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack direction="row" gap="20px">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "350px",
                pr: "20px",
              }}
            >
              <Avatar
                src={company.logo || ""}
                alt={company.name}
                sx={{
                  width: 150,
                  height: 150,
                  mb: 2,
                  mt: "20px",
                  border: "1px dashed #c9c9c9",
                  backgroundColor: "#262626",
                }}
              >
                {<CameraIcon color="#FDDF59" width={40} height={40} />}
              </Avatar>

              <Button
                component="label"
                variant="outlined"
                startIcon={
                  <CameraIcon color="#FDDF59" width={22} height={22} />
                }
                // sx={{ mb: 1 }}
                sx={{
                  borderRadius: "10px",
                  px: "26px",
                  minWidth: "auto",
                  width: "auto",
                  textTransform: "none",
                  bgcolor: "transparent",
                  color: "#fddf59",
                  boxShadow: "none",
                  py: "10px",
                  fontSize: "16px",
                  lineHeight: "22px",
                  fontFamily: "Inter",
                  fontWeight: "500",
                  border: "1px solid #c9c9c9",
                }}
              >
                Upload Logo
                <input type="file" hidden accept="image/*" />
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  fontWeight: "400",
                  fontSize: "18px",
                  lineHeight: "21.78px",
                  color: "#696868",
                  pt: "8px",
                }}
              >
                Recommended size: 400x400 pixels
              </Typography>
            </Box>

            <Stack sx={{ width: "100%" }}>
              <Stack>
                <Stack>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {" "}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "400",
                        fontSize: "18px",
                        lineHeight: "21.78px",
                        // color: "#696868",
                        color: "#c9c9c9",
                        // pb: "8px",
                      }}
                    >
                      Company Name
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Pitchers"
                      // sx={{
                      //   // width: "400px",
                      //   // minWidth: "200px",
                      //   input: {
                      //     bgcolor: "#ffffff",
                      //     color: "#696868",
                      //     fontSize: "18px",
                      //     boxSizing: "content-box",
                      //     px: "4px",
                      //     py: "12px",
                      //     "&::placeholder": {
                      //       color: "#696868",
                      //     },
                      //   },
                      //   "& .MuiOutlinedInput-root": {
                      //     px: "8px",
                      //     borderRadius: "12px",
                      //     bgcolor: "#ffffff",
                      //     "& fieldset": {
                      //       border: "1px solid #e3e3e3",
                      //     }, // Outline color
                      //     "&:hover fieldset": {
                      //       border: "1px solid #e3e3e3",
                      //     }, // Hover state
                      //     "&.Mui-focused fieldset": {
                      //       border: "1px solid #e3e3e3",
                      //     }, // Focus state
                      //   },
                      // }}
                      sx={{
                        // width: "400px",
                        // minWidth: "200px",
                        input: {
                          bgcolor: "#262626",
                          color: "#c9c9c9",
                          fontSize: "18px",
                          boxSizing: "content-box",
                          px: "4px",
                          py: "12px",
                          "&::placeholder": {
                            color: "#c9c9c9",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          px: "8px",
                          borderRadius: "12px",
                          bgcolor: "#262626",
                          "& fieldset": {
                            border: "2px solid #FFD700",
                          }, // Outline color
                          "&:hover fieldset": {
                            border: "2px solid #FFD700",
                          }, // Hover state
                          "&.Mui-focused fieldset": {
                            border: "2px solid #FFD700",
                          }, // Focus state
                        },
                      }}
                    />
                  </Box>
                  <Stack direction="row" sx={{ gap: "20px", py: "20px" }}>
                    {" "}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flex: 1,
                        // py: "60px",
                      }}
                    >
                      {" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "400",
                          fontSize: "18px",
                          lineHeight: "21.78px",
                          color: "#c9c9c9",
                          // pb: "8px",
                        }}
                      >
                        Industry
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Information Technology"
                        sx={{
                          flex: 1,
                          // width: "440px",
                          // minWidth: "200px",
                          input: {
                            bgcolor: "#262626",
                            color: "#c9c9c9",
                            fontSize: "18px",
                            boxSizing: "content-box",
                            px: "4px",
                            py: "12px",
                            "&::placeholder": {
                              color: "#c9c9c9",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            px: "8px",
                            borderRadius: "12px",
                            bgcolor: "#262626",
                            "& fieldset": {
                              border: "2px solid #FFD700",
                            }, // Outline color
                            "&:hover fieldset": {
                              border: "2px solid #FFD700",
                            }, // Hover state
                            "&.Mui-focused fieldset": {
                              border: "2px solid #FFD700",
                            }, // Focus state
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flex: 1,
                        // py: "60px",
                      }}
                    >
                      {" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "400",
                          fontSize: "18px",
                          lineHeight: "21.78px",
                          color: "#c9c9c9",
                          // pb: "8px",
                        }}
                      >
                        Company address
                      </Typography>
                      <TextField
                        placeholder="California"
                        variant="outlined"
                        fullWidth
                        sx={{
                          flex: 1,
                          // width: "440px",
                          // minWidth: "200px",
                          input: {
                            bgcolor: "#262626",
                            color: "#c9c9c9",
                            fontSize: "18px",
                            boxSizing: "content-box",
                            px: "4px",
                            py: "12px",
                            "&::placeholder": {
                              color: "#c9c9c9",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            px: "8px",
                            borderRadius: "12px",
                            bgcolor: "#262626",
                            "& fieldset": {
                              border: "2px solid #FFD700",
                            }, // Outline color
                            "&:hover fieldset": {
                              border: "2px solid #FFD700",
                            }, // Hover state
                            "&.Mui-focused fieldset": {
                              border: "2px solid #FFD700",
                            }, // Focus state
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                  <Stack direction="row" sx={{ gap: "20px", pb: "20px" }}>
                    {" "}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flex: 1,
                        // py: "60px",
                      }}
                    >
                      {" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "400",
                          fontSize: "18px",
                          lineHeight: "21.78px",
                          color: "#c9c9c9",
                          // pb: "8px",
                        }}
                      >
                        Company Email
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="abc@gmail.com"
                        sx={{
                          // width: "440px",
                          // minWidth: "200px",
                          input: {
                            bgcolor: "#262626",
                            color: "#c9c9c9",
                            fontSize: "18px",
                            boxSizing: "content-box",
                            px: "4px",
                            py: "12px",
                            "&::placeholder": {
                              color: "#c9c9c9",
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            px: "8px",
                            borderRadius: "12px",
                            bgcolor: "#262626",
                            "& fieldset": {
                              border: "2px solid #FFD700",
                            }, // Outline color
                            "&:hover fieldset": {
                              border: "2px solid #FFD700",
                            }, // Hover state
                            "&.Mui-focused fieldset": {
                              border: "2px solid #FFD700",
                            }, // Focus state
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flex: 1,
                        // py: "60px",
                      }}
                    >
                      {" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "400",
                          fontSize: "18px",
                          lineHeight: "21.78px",
                          color: "#c9c9c9",
                          // pb: "8px",
                        }}
                      >
                        Company Contact
                      </Typography>
                      <PhoneInput
                        country={"us"}
                        // value={phone}
                        // onChange={(phone) => setPhone(phone)}
                        inputStyle={{
                          width: "100%",
                          height: "56px",
                          borderRadius: "12px",
                          paddingLeft: "48px",
                          fontSize: "18px",
                          background: "#262626",
                          color: "#c9c9c9",
                          border: "2px solid #FFD700",
                          opacity: 1,
                        }}
                        containerStyle={{
                          // width: "440px",
                          // minWidth: "200px",
                          borderRadius: "12px",
                          background: "#262626",
                        }}
                        buttonStyle={{
                          background: "transparent",
                          border: "none",
                        }}
                      />
                    </Box>
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      pb: "20px",
                    }}
                  >
                    {" "}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "400",
                        fontSize: "18px",
                        lineHeight: "21.78px",
                        color: "#c9c9c9",
                        // pb: "8px",
                      }}
                    >
                      Website
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="https://example.com"
                      sx={{
                        // width: "400px",
                        // minWidth: "200px",
                        input: {
                          bgcolor: "#262626",
                          color: "#c9c9c9",
                          fontSize: "18px",
                          boxSizing: "content-box",
                          px: "4px",
                          py: "12px",
                          "&::placeholder": {
                            color: "#c9c9c9",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          px: "8px",
                          borderRadius: "12px",
                          bgcolor: "#262626",
                          "& fieldset": {
                            border: "2px solid #FFD700",
                          }, // Outline color
                          "&:hover fieldset": {
                            border: "2px solid #FFD700",
                          }, // Hover state
                          "&.Mui-focused fieldset": {
                            border: "2px solid #FFD700",
                          }, // Focus state
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {" "}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "400",
                        fontSize: "18px",
                        lineHeight: "21.78px",
                        color: "#c9c9c9",
                        // pb: "8px",
                      }}
                    >
                      Description
                    </Typography>
                    <TextField
                      variant="outlined"
                      placeholder="Description ..."
                      fullWidth
                      multiline
                      rows={5} // Set the number of visible lines
                      sx={{
                        input: {
                          bgcolor: "#262626",
                          color: "#c9c9c9",
                          fontSize: "18px",
                          boxSizing: "content-box",
                          px: "4px",
                          py: "12px",
                          "&::placeholder": {
                            color: "#c9c9c9",
                          },
                        },
                        "& .MuiOutlinedInput-root": {
                          px: "8px",
                          borderRadius: "12px",
                          bgcolor: "#262626",
                          "& fieldset": {
                            border: "2px solid #FFD700",
                          }, // Outline color
                          "&:hover fieldset": {
                            border: "2px solid #FFD700",
                          }, // Hover state
                          "&.Mui-focused fieldset": {
                            border: "2px solid #FFD700",
                          }, // Focus state
                        },
                      }}
                    />
                  </Box>

                  {/* <TextField
                  fullWidth
                  placeholder="Company Name"
                  name="name"
                  value={company.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={{
                    input: {
                      color: "#ffffff",
                      fontSize: "18px",
                      boxSizing: "content-box",
                      px: "4px",
                      py: "16px",
                      "&::placeholder": {
                        color: "#ffffff",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      // height: "154px",
                      px: "8px",
                      borderRadius: "12px",
                      bgcolor: "#333333",
                      "& fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Outline color
                      "&:hover fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Hover state
                      "&.Mui-focused fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Focus state
                    },
                  }}
                /> */}
                </Stack>

                {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  //   label="Industry"
                  name="industry"
                  value={company.industry}
                  onChange={handleChange}
                  error={!!errors.industry}
                  helperText={errors.industry}
                  required
                  sx={{
                    input: {
                      color: "#ffffff",
                      fontSize: "18px",
                      boxSizing: "content-box",
                      px: "4px",
                      py: "12px",
                      "&::placeholder": {
                        color: "#ffffff",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      // height: "154px",
                      px: "8px",
                      borderRadius: "12px",
                      bgcolor: "#333333",
                      "& fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Outline color
                      "&:hover fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Hover state
                      "&.Mui-focused fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Focus state
                    },
                  }}
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid> */}
                {/* <TextField
          variant="outlined"
          fullWidth
          multiline
          rows={5} // Set the number of visible lines
          sx={{
            input: {
              bgcolor: "#ffffff",
              color: "#696868",
              fontSize: "18px",
              boxSizing: "content-box",
              px: "4px",
              py: "12px",
              "&::placeholder": {
                color: "#696868",
              },
            },
            "& .MuiOutlinedInput-root": {
              // height: "154px",
              px: "8px",
              borderRadius: "12px",
              bgcolor: "#ffffff",
              "& fieldset": {
                border: "1px solid #e3e3e3",
              }, // Outline color
              "&:hover fieldset": {
                border: "1px solid #e3e3e3",
              }, // Hover state
              "&.Mui-focused fieldset": {
                border: "1px solid #e3e3e3",
              }, // Focus state
            },
          }}
        /> */}
                {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="location"
                  value={company.location}
                  onChange={handleChange}
                  error={!!errors.location}
                  helperText={errors.location}
                  required
                  placeholder="City, State/Country"
                  sx={{
                    input: {
                      bgcolor: "transparent",
                      color: "#ffffff",
                      fontSize: "18px",
                      boxSizing: "content-box",
                      px: "4px",
                      py: "16px",
                      "&::placeholder": {
                        color: "#ffffff",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      // height: "154px",
                      px: "8px",
                      borderRadius: "12px",
                      bgcolor: "#333333",
                      "& fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Outline color
                      "&:hover fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Hover state
                      "&.Mui-focused fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Focus state
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="website"
                  value={company.website}
                  onChange={handleChange}
                  error={!!errors.website}
                  helperText={errors.website}
                  placeholder="https://example.com"
                  sx={{
                    input: {
                      bgcolor: "transparent",
                      color: "#ffffff",
                      fontSize: "18px",
                      boxSizing: "content-box",
                      px: "4px",
                      py: "16px",
                      "&::placeholder": {
                        color: "#ffffff",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      // height: "154px",
                      px: "8px",
                      borderRadius: "12px",
                      bgcolor: "#333333",
                      "& fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Outline color
                      "&:hover fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Hover state
                      "&.Mui-focused fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Focus state
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  value={company.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  required
                  multiline
                  rows={4}
                  placeholder="Tell us about your company..."
                  sx={{
                    input: {
                      bgcolor: "transparent",
                      color: "#ffffff",
                      fontSize: "18px",
                      boxSizing: "content-box",
                      px: "4px",
                      py: "16px",
                      "&::placeholder": {
                        color: "#ffffff",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      // height: "154px",
                      px: "8px",
                      borderRadius: "12px",
                      bgcolor: "#333333",
                      "& fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Outline color
                      "&:hover fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Hover state
                      "&.Mui-focused fieldset": {
                        border: "1px solid #e3e3e3",
                      }, // Focus state
                    },
                  }}
                />
              </Grid> */}
              </Stack>
            </Stack>
          </Stack>
          <Stack>
            <Stack>
              <Divider sx={{ my: 5, bgcolor: "#FFFFFF" }} />

              {/* <Typography variant="h6" gutterBottom sx={{ color: "#ffffff" }}>
              Team Members
            </Typography> */}

              <Typography
                variant="body2"
                sx={{
                  color: "black",
                  pb: "10px",
                  fontSize: "20px",
                  lineHeight: "22px",
                  fontFamily: "Inter",
                  fontWeight: "700",
                  color: "#ffffff",
                }}
              >
                Add Team Members
              </Typography>

              <Stack direction="row" sx={{ gap: "20px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    flex: 2,
                  }}
                >
                  {" "}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "400",
                      fontSize: "18px",
                      lineHeight: "21.78px",
                      color: "#c9c9c9",
                      // pb: "8px",
                    }}
                  >
                    Member Email
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="asdksdfjdjf@gmail.com"
                    sx={{
                      // width: "400px",
                      // minWidth: "200px",
                      input: {
                        bgcolor: "#262626",
                        color: "#c9c9c9",
                        fontSize: "18px",
                        boxSizing: "content-box",
                        px: "4px",
                        py: "12px",
                        "&::placeholder": {
                          color: "#c9c9c9",
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        px: "8px",
                        borderRadius: "12px",
                        bgcolor: "#262626",
                        "& fieldset": {
                          border: "2px solid #FFD700",
                        }, // Outline color
                        "&:hover fieldset": {
                          border: "2px solid #FFD700",
                        }, // Hover state
                        "&.Mui-focused fieldset": {
                          border: "2px solid #FFD700",
                        }, // Focus state
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    flex: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "400",
                      fontSize: "18px",
                      lineHeight: "21.78px",
                      color: "#c9c9c9",
                      // pb: "8px",
                    }}
                  >
                    Select Member Role
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    name="role"
                    value={newMember.role}
                    onChange={handleNewMemberChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#262626",
                        color: "#c9c9c9", // Applies text color
                        fontSize: "18px",
                        "& .MuiSelect-select": {
                          px: "10px",
                          py: "14px",
                          pl: "14px",
                          color: "#c9c9c9",
                          bgcolor: "#262626",
                        },
                        "& fieldset": {
                          border: "2px solid #FFD700",
                        },
                        "&:hover fieldset": {
                          border: "2px solid #FFD700",
                        },
                        "&.Mui-focused fieldset": {
                          border: "2px solid #FFD700",
                        },
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#c9c9c9", // Color of dropdown arrow
                      },
                    }}
                  >
                    <MenuItem value="admin">Administrator</MenuItem>
                    <MenuItem value="hr">HR Manager</MenuItem>
                    <MenuItem value="staff">Staff Member</MenuItem>
                  </TextField>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon color="#FDDF59" />}
                  onClick={handleAddMember}
                  // sx={{
                  //   fontSize: "16px",
                  //   lineHeight: "22px",
                  //   color: "#FFFFFF",
                  //   py: "10px",
                  //   textTransform: "none",
                  //   bgcolor: "#1E1E1E",
                  //   borderRadius: "10px",
                  //   mt: "20px",
                  //   px: "50px",
                  // }}
                  // sx={{
                  //   borderRadius: "28px",
                  //   px: "20px",
                  //   minWidth: "auto",
                  //   width: "auto",
                  //   textTransform: "none",
                  //   bgcolor: "#000000",
                  //   color: "#FFFFFF",
                  //   boxShadow: "none",
                  //   py: "8px",
                  //   mt: "30px",
                  //   "&:hover": {
                  //     backgroundColor: "#000000",
                  //     boxShadow: "none",
                  //   },
                  //   "&:active": {
                  //     backgroundColor: "#000000",
                  //     boxShadow: "none",
                  //   },
                  //   "&:focus": {
                  //     outline: "none",
                  //     boxshadow: "none",
                  //   },
                  //   "& .MuiButtonBase-root": {
                  //     width: "auto !important",
                  //     minWidth: "0px !important",
                  //     padding: "0px",
                  //     boxshadow: "none",
                  //   },
                  // }}
                  sx={{
                    fontSize: "16px",
                    lineHeight: "22px",
                    color: "#FDDF59",
                    py: "16px",
                    textTransform: "none",
                    bgcolor: "#1E1E1E",
                    borderRadius: "10px",
                    mt: "30px",
                    // px: "50px",
                    border: "1px solid #c9c9c9",
                    flex: 0.8,
                    // fontWeight: "700",
                  }}
                >
                  Add Team Member
                </Button>

                {/* <SaveChangingsButton>Save Changings</SaveChangingsButton> */}
              </Stack>

              <Box sx={{ mt: 2 }}>
                {company.teamMembers.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}
                  >
                    {company.teamMembers.map((member) => (
                      <Chip
                        key={member.id}
                        label={`${member.email} (${member.role})`}
                        onDelete={() => handleRemoveMember(member.id)}
                        deleteIcon={<DeleteIcon />}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, color: "#696868" }}
                  >
                    No team members added yet.
                  </Typography>
                )}
              </Box>
            </Stack>

            <Grid item xs={12} sx={{ width: "100%" }}>
              <Divider sx={{ my: 2, bgcolor: "#FFFFFF" }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    // In a real app, this would navigate back or clear the form
                    if (!editMode) {
                      setCompany({
                        name: "",
                        description: "",
                        industry: "",
                        location: "",
                        website: "",
                        logo: "",
                        teamMembers: [],
                      });
                    }
                  }}
                  sx={{
                    fontSize: "16px",
                    lineHeight: "22px",
                    color: "#FDDF59",
                    py: "14px",
                    textTransform: "none",
                    bgcolor: "#1E1E1E",
                    borderRadius: "10px",
                    mt: "20px",
                    px: "50px",
                    border: "1px solid #c9c9c9",
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    fontSize: "16px",
                    lineHeight: "22px",
                    color: "#FDDF59",
                    py: "14px",
                    textTransform: "none",
                    bgcolor: "#1E1E1E",
                    borderRadius: "10px",
                    mt: "20px",
                    px: "50px",
                    border: "1px solid #c9c9c9",
                  }}
                >
                  {editMode ? "Save Changes" : "Create Company"}
                </Button>
              </Box>
            </Grid>
          </Stack>
        </form>
        <JoinPitchersDark />
      </Stack>

      <FooterSection />
    </>
  );
};

export default CreateCompany;
